import { useCallback, useEffect, useState } from "react";
import { Download, Droplets, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { hoyMX } from "@/lib/utils";

const TIPO = {
  merma: { label: "Merma", cls: "chip-rose" },
  ajuste_mas: { label: "Ajuste +", cls: "chip-emerald" },
  ajuste_menos: { label: "Ajuste −", cls: "chip-amber" },
};
const mxn = (n) => `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const HistorialMermas = ({ refresco, sucursales }) => {
  const [mes, setMes] = useState(hoyMX().slice(0, 7));
  const [sucursalId, setSucursalId] = useState("");
  const [tipo, setTipo] = useState("");
  const [rep, setRep] = useState({ filas: [], totales: {}, por_sucursal: [] });
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get("/mermas/reporte-mensual", { params: { mes, ...(sucursalId ? { sucursal_id: sucursalId } : {}) } });
      setRep(r.data);
    } catch {} finally { setCargando(false); }
  }, [mes, sucursalId]);

  useEffect(() => { cargar(); }, [cargar, refresco]);

  const filas = tipo ? rep.filas.filter((f) => f.tipo === tipo) : rep.filas;
  const t = rep.totales || {};

  function csv() {
    const cab = ["Fecha", "Tipo", "Sucursal", "SKU", "Artículo", "Cantidad", "Unidad", "Costo", "Motivo / Observación", "Registró", "Autorizó"];
    const lineas = filas.map((f) => [new Date(f.fecha).toLocaleString("es-MX"), TIPO[f.tipo]?.label, f.sucursal, f.sku || "", f.articulo, f.cantidad, f.unidad || "", f.costo ?? "", (f.motivo || "").replace(/"/g, "'"), f.usuario || "", f.autorizo || ""]);
    const txt = [cab, ...lineas].map((l) => l.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + txt], { type: "text/csv;charset=utf-8" }));
    a.download = `mermas_ajustes_${mes}.csv`;
    a.click();
  }

  return (
    <div className="space-y-4" data-testid="historial-mermas">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="eyebrow flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Mes</label>
          <input data-testid="historial-mes-input" type="month" value={mes} onChange={(e) => setMes(e.target.value)} className="field mt-1 font-mono" />
        </div>
        {sucursales.length > 0 && (
          <div>
            <label className="eyebrow">Sucursal</label>
            <select data-testid="historial-sucursal-select" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)} className="field mt-1">
              <option value="">Todas</option>
              {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
          </div>
        )}
        <div className="flex gap-1 bg-brand-surface p-1 rounded-full">
          {[["", `Todo (${rep.filas.length})`], ["merma", `Mermas (${t.merma?.registros || 0})`], ["ajuste_mas", `Ajustes + (${t.ajuste_mas?.registros || 0})`], ["ajuste_menos", `Ajustes − (${t.ajuste_menos?.registros || 0})`]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`historial-tipo-${v || "todo"}`} onClick={() => setTipo(v)} className={`px-3 h-9 rounded-full text-xs font-semibold transition-colors duration-150 ${tipo === v ? "bg-brand-ink text-white" : "text-brand-slate hover:text-brand-ink"}`}>{l}</button>
          ))}
        </div>
        <button type="button" data-testid="historial-csv-button" onClick={csv} className="btn-soft h-9 ml-auto"><Download className="w-4 h-4" /> CSV mensual</button>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="historial-totales">
        {[["merma", "Mermas del mes", "text-rose-700"], ["ajuste_mas", "Ajustes (+)", "text-emerald-700"], ["ajuste_menos", "Ajustes (−)", "text-amber-700"]].map(([k, l, c]) => (
          <div key={k} className="surface-card p-4">
            <div className="eyebrow">{l}</div>
            <div className={`font-display text-2xl font-extrabold ${c}`}>{t[k]?.registros || 0}</div>
            <div className="text-xs text-brand-muted font-mono">costo {mxn(t[k]?.costo)}</div>
          </div>
        ))}
      </div>

      {rep.por_sucursal?.length > 1 && (
        <div className="surface-card overflow-hidden" data-testid="historial-por-sucursal">
          <table className="w-full text-sm">
            <thead><tr className="table-head"><th className="px-4 py-2">Sucursal</th><th className="px-4 py-2 text-right">Mermas</th><th className="px-4 py-2 text-right">Ajustes +</th><th className="px-4 py-2 text-right">Ajustes −</th><th className="px-4 py-2 text-right">Costo mermas</th><th className="px-4 py-2 text-right">Neto ajustes</th></tr></thead>
            <tbody>
              {rep.por_sucursal.map((s) => (
                <tr key={s.sucursal} className="border-t border-brand-line/70"><td className="px-4 py-2 font-semibold text-brand-ink">{s.sucursal}</td><td className="px-4 py-2 text-right font-mono text-xs">{s.mermas}</td><td className="px-4 py-2 text-right font-mono text-xs">{s.ajustes_mas}</td><td className="px-4 py-2 text-right font-mono text-xs">{s.ajustes_menos}</td><td className="px-4 py-2 text-right font-mono text-xs text-rose-700">{mxn(s.costo_mermas)}</td><td className="px-4 py-2 text-right font-mono text-xs">{mxn(s.costo_ajustes)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="surface-card overflow-hidden" data-testid="mermas-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              <th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Artículo</th><th className="px-4 py-3 text-right">Cantidad</th><th className="px-4 py-3 text-right">Costo</th><th className="px-4 py-3">Motivo / Observación</th><th className="px-4 py-3">Registró</th><th className="px-4 py-3">Fecha</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.id} className="border-t border-brand-line/70" data-testid={`historial-row-${f.id}`}>
                <td className="px-4 py-2"><span className={`chip whitespace-nowrap ${TIPO[f.tipo]?.cls}`}>{TIPO[f.tipo]?.label}</span></td>
                <td className="px-4 py-2">
                  <div className="font-semibold text-brand-ink flex items-center gap-1.5">{f.tipo === "merma" && f.foto && <Droplets className="w-3.5 h-3.5 text-brand-muted" />}{f.articulo}</div>
                  <div className="text-[10px] uppercase tracking-wider text-brand-muted">{f.sku || ""}{sucursales.length > 0 ? ` · ${f.sucursal}` : ""}</div>
                </td>
                <td className={`px-4 py-2 text-right font-mono text-xs font-bold ${f.cantidad >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{f.cantidad >= 0 ? "+" : ""}{f.cantidad} {f.unidad}{f.stock_anterior != null && <div className="text-[10px] text-brand-muted font-normal">{f.stock_anterior} → {f.stock_nuevo}</div>}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-brand-slate">{mxn(f.costo)}</td>
                <td className="px-4 py-2 text-xs text-brand-slate max-w-[240px]">{f.motivo}</td>
                <td className="px-4 py-2 text-xs text-brand-slate">{f.usuario}{f.autorizo && <div className="text-[10px] text-brand-muted">autorizó {f.autorizo}</div>}</td>
                <td className="px-4 py-2 text-xs text-brand-muted font-mono">{new Date(f.fecha).toLocaleString("es-MX")}</td>
              </tr>
            ))}
            {filas.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-brand-muted">{cargando ? "Cargando…" : "Sin registros en este mes."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistorialMermas;
